// frontend/src/components/SupplierPaymentList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  Receipt, Building, Truck, Calendar, DollarSign, CreditCard,
  Eye, Edit, Trash2, Plus, Search, Filter, Download, RefreshCw,
  Loader2, X, ChevronDown, Globe, CheckCircle, Clock, XCircle,
  AlertCircle, Wallet, Smartphone, ChevronLeft, ChevronRight,
  Banknote, CircleDollarSign, FileText, Hash, User
} from "lucide-react";
import "./SupplierPaymentList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const SupplierPaymentList = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [stats, setStats] = useState({
    totalPayments: 0,
    totalAmount: 0,
    totalDueAmount: 0,
    processPayments: 0,
    completedPayments: 0,
    paidPayments: 0,
    partialPayments: 0,
    notpaidPayments: 0
  });

  const [filter, setFilter] = useState({
    payment_status: "",
    status: "",
    supplier_id: "",
    start_date: "",
    end_date: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && currentUser?.role === 'admin';

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
    if (isSystemAdmin) fetchCompanies();
  }, [selectedCompany, currentPage, filter]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      if (filter.payment_status) queryParams.append("payment_status", filter.payment_status);
      if (filter.status) queryParams.append("status", filter.status);
      if (filter.supplier_id) queryParams.append("supplier_id", filter.supplier_id);
      if (filter.start_date) queryParams.append("start_date", filter.start_date);
      if (filter.end_date) queryParams.append("end_date", filter.end_date);
      if (isSystemAdmin && selectedCompany) queryParams.append("company_id", selectedCompany);

      const response = await axios.get(`${url}/api/supplierpayment?${queryParams}`);

      if (response.data.success) {
        setPayments(response.data.data);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || response.data.data.length);
        if (response.data.stats) setStats(response.data.stats);
      }
    } catch (error) {
      toast.error("Failed to load supplier payments");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const params = {};
      if (isSystemAdmin && selectedCompany) params.company_id = selectedCompany;
      const response = await axios.get(`${url}/api/supplier/dropdown`, { params });
      if (response.data.success) setSuppliers(response.data.data || []);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${url}/api/company`);
      if (response.data.success) setCompanies(response.data.data || []);
    } catch (error) {
      // Silent fail
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchPayments();
  };

  const resetFilters = () => {
    setFilter({
      payment_status: "",
      status: "",
      supplier_id: "",
      start_date: "",
      end_date: "",
    });
    setSelectedCompany("");
    setSearchTerm("");
    setCurrentPage(1);
    fetchPayments();
  };

  const handleEdit = (id) => navigate(`/supplierpayment/edit/${id}`);
  const handleCreate = () => navigate("/AddSupplierPayment");

  const handleDelete = async (id, paymentNo) => {
    if (!window.confirm(`Cancel payment "${paymentNo}"?`)) return;
    try {
      await axios.delete(`${url}/api/supplierpayment/${id}`);
      toast.success(`Payment cancelled`);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel");
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${url}/api/supplierpayment/${id}/approve`, {});
      toast.success("Payment approved");
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to approve");
    }
  };

  const handleSubmit = async (id) => {
    try {
      await axios.put(`${url}/api/supplierpayment/${id}/submit`, {});
      toast.success("Payment submitted");
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit");
    }
  };

  const handleExport = () => {
    if (payments.length === 0) {
      toast.warning("No data to export");
      return;
    }
    try {
      const headers = ["Payment No","Date","Supplier","Invoice No","Invoice Amount","Due Amount","Payment Amount","Payment Status","Status","Currency"];
      const csvData = payments.map(p => {
        const originalTotal = p.total_invoice_amount || p.purchase_invoice_id?.grand_total || 0;
        const dueAfter = Math.max(0, (p.total_due_amount || 0) - (p.total_paying_now || 0));
        return [
          p.payment_no || "",
          new Date(p.payment_date).toLocaleDateString(),
          p.supplier_id?.supplier_name || "N/A",
          p.purchase_invoice_id?.invoice_no || "N/A",
          originalTotal,
          dueAfter,
          p.total_paying_now || 0,
          p.payment_status || "N/A",
          p.status || "N/A",
          p.currency || "USD"
        ];
      });
      const csvContent = [headers.join(','), ...csvData.map(row => row.map(cell => typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell).join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `supplier-payments-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Export started");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "$0.00";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return "Invalid date"; }
  };

  const getPaymentStatusBadge = (status) => {
    const config = {
      paid: { bg: '#d4edda', color: '#155724', icon: CheckCircle, label: 'Paid' },
      partial: { bg: '#cce5ff', color: '#004085', icon: AlertCircle, label: 'Partial' },
      notpaid: { bg: '#f8d7da', color: '#721c24', icon: XCircle, label: 'Not Paid' },
      pending: { bg: '#fff3cd', color: '#856404', icon: Clock, label: 'Pending' },
      cancelled: { bg: '#f8d7da', color: '#721c24', icon: XCircle, label: 'Cancelled' }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, backgroundColor: c.bg, color: c.color }}><Icon size={12} />{c.label}</span>;
  };

  const getStatusBadge = (status) => {
    const config = {
      process: { bg: '#cce5ff', color: '#004085', label: 'Process' },
      completed: { bg: '#d4edda', color: '#155724', label: 'Completed' },
      cancelled: { bg: '#f8d7da', color: '#721c24', label: 'Cancelled' }
    };
    const c = config[status] || config.process;
    return <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, backgroundColor: c.bg, color: c.color }}>{c.label}</span>;
  };

  const getPaymentMethodIcon = (method) => {
    switch(method) {
      case 'cash': return <CreditCard size={12} />;
      case 'evc': return <Smartphone size={12} />;
      case 'edahab': return <Wallet size={12} />;
      case 'bank_transfer': return <Banknote size={12} />;
      case 'credit': return <CircleDollarSign size={12} />;
      default: return <DollarSign size={12} />;
    }
  };

  // Filter payments by search term
  const filteredPayments = payments.filter(p => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (p.payment_no && p.payment_no.toLowerCase().includes(searchLower)) ||
      (p.supplier_id?.supplier_name || "").toLowerCase().includes(searchLower) ||
      (p.purchase_invoice_id?.invoice_no || "").toLowerCase().includes(searchLower);
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);
  const filteredTotalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  if (loading) return <div className="loading-container"><Loader2 className="animate-spin" size={48} /><p>Loading supplier payments...</p></div>;

  return (
    <div className="payment-list-container">
      {/* Header */}
      <div className="header-section">
        <div className="title-section">
          <Receipt size={28} />
          <div>
            <h1>Supplier Payments</h1>
            <p className="company-info">{isSystemAdmin ? <><Globe size={14} /> All Companies{selectedCompany && companies.find(c => c._id === selectedCompany) && <> - {companies.find(c => c._id === selectedCompany)?.company_name}</>}</> : <><Building size={14} /> {company?.company_name} ({company?.company_code})</>}</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}><Filter size={16} />{showFilters ? 'Hide Filters' : 'Show Filters'}</button>
          <button className="export-btn" onClick={handleExport}><Download size={16} />Export</button>
          <button className="refresh-btn" onClick={fetchPayments}><RefreshCw size={16} />Refresh</button>
          <button className="create-btn" onClick={handleCreate}><Plus size={16} />New Payment</button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-icon blue"><Receipt size={20} /></div><div className="stat-content"><span className="stat-value">{stats.totalPayments}</span><span className="stat-label">Total Payments</span></div></div>
        <div className="stat-card"><div className="stat-icon green"><DollarSign size={20} /></div><div className="stat-content"><span className="stat-value">{formatCurrency(stats.totalAmount)}</span><span className="stat-label">Total Paid</span></div></div>
        <div className="stat-card"><div className="stat-icon teal"><AlertCircle size={20} /></div><div className="stat-content"><span className="stat-value">{stats.partialPayments}</span><span className="stat-label">Partial</span></div></div>
        <div className="stat-card"><div className="stat-icon orange"><XCircle size={20} /></div><div className="stat-content"><span className="stat-value">{stats.notpaidPayments}</span><span className="stat-label">Not Paid</span></div></div>
        <div className="stat-card"><div className="stat-icon purple"><Clock size={20} /></div><div className="stat-content"><span className="stat-value">{stats.processPayments}</span><span className="stat-label">Process</span></div></div>
        <div className="stat-card"><div className="stat-icon success"><CheckCircle size={20} /></div><div className="stat-content"><span className="stat-value">{stats.completedPayments}</span><span className="stat-label">Completed</span></div></div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-header"><Filter size={18} /><span>Filter Payments</span><button onClick={resetFilters} className="clear-filters">Clear All</button></div>
          <div className="filters-grid">
            {isSystemAdmin && <div className="filter-group"><label>Company</label><select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)}><option value="">All Companies</option>{companies.map(comp => <option key={comp._id} value={comp._id}>{comp.company_name} ({comp.company_code})</option>)}</select></div>}
            <div className="filter-group"><label>Payment Status</label><select name="payment_status" value={filter.payment_status} onChange={handleFilterChange}><option value="">All</option><option value="paid">Paid</option><option value="partial">Partial</option><option value="notpaid">Not Paid</option><option value="pending">Pending</option><option value="cancelled">Cancelled</option></select></div>
            <div className="filter-group"><label>Document Status</label><select name="status" value={filter.status} onChange={handleFilterChange}><option value="">All</option><option value="process">Process</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select></div>
            <div className="filter-group"><label>Supplier</label><select name="supplier_id" value={filter.supplier_id} onChange={handleFilterChange}><option value="">All Suppliers</option>{suppliers.map(s => <option key={s._id} value={s._id}>{s.supplier_code} - {s.supplier_name}</option>)}</select></div>
            <div className="filter-group"><label>Start Date</label><input type="date" name="start_date" value={filter.start_date} onChange={handleFilterChange} /></div>
            <div className="filter-group"><label>End Date</label><input type="date" name="end_date" value={filter.end_date} onChange={handleFilterChange} /></div>
          </div>
          <div className="filters-actions"><button onClick={applyFilters} className="apply-filters"><Filter size={16} /> Apply Filters</button></div>
        </div>
      )}

      {/* Search */}
      <div className="search-section">
        <div className="search-box"><Search size={18} /><input type="text" placeholder="Search by payment no, supplier, invoice no..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />{searchTerm && <button className="clear-search" onClick={() => setSearchTerm("")}><X size={14} /></button>}</div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Payment No</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>Invoice No</th>
              <th>Invoice Amount</th>
              <th>Payment Amount</th>
              <th>Due Amount</th>
              <th>Payment Methods</th>
              <th>Payment Status</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentPayments.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">
                  <div className="empty-state">
                    <Receipt size={48} color="#ccc" />
                    <p>No supplier payments found</p>
                    <button onClick={handleCreate} className="create-btn"><Plus size={16} /> Create First Payment</button>
                  </div>
                </td>
              </tr>
            ) : (
              currentPayments.map((payment) => {
                const invoiceTotal = payment.total_invoice_amount || payment.purchase_invoice_id?.grand_total || 0;
                const paymentAmount = payment.total_paying_now || 0;
                const dueBefore = payment.total_due_amount || 0;
                const dueAfter = Math.max(0, dueBefore - paymentAmount);

                return (
                  <tr key={payment._id}>
                    <td><div className="payment-no"><Hash size={12} /><span>{payment.payment_no}</span></div></td>
                    <td>{formatDate(payment.payment_date)}</td>
                    <td><div className="supplier-info"><Truck size={14} /><span>{payment.supplier_id?.supplier_name || 'N/A'}</span><small>{payment.supplier_id?.supplier_code || ''}</small></div></td>
                    <td><span className="invoice-no">{payment.purchase_invoice_id?.invoice_no || 'N/A'}</span></td>
                    <td className="amount invoice-amount">{formatCurrency(invoiceTotal)}</td>
                    <td className="amount paid-amount highlight" style={{ fontWeight: 600, color: '#1890ff' }}>{formatCurrency(paymentAmount)}</td>
                    <td className="amount due-amount" style={{ color: dueAfter > 0 ? '#ff4d4f' : '#52c41a', fontWeight: 600, backgroundColor: dueAfter > 0 ? '#fff1f0' : '#f6ffed', padding: '4px 8px', borderRadius: '4px' }}>{formatCurrency(dueAfter)}</td>
                    <td><div className="payment-methods">{payment.payment_methods?.map((pm, i) => <span key={i} className="method-badge" title={`${pm.method}: ${pm.amount}`}>{getPaymentMethodIcon(pm.method)}<span className="method-amount">{formatCurrency(pm.amount)}</span></span>)}</div></td>
                    <td>{getPaymentStatusBadge(payment.payment_status)}</td>
                    <td>{getStatusBadge(payment.status)}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="edit-btn" onClick={() => handleEdit(payment._id)} title="Edit" disabled={payment.status === 'completed' || payment.status === 'cancelled'}><Edit size={16} /></button>
                        {payment.status === 'process' && <button className="submit-btn" onClick={() => handleSubmit(payment._id)} title="Submit"><CheckCircle size={16} /></button>}
                        <button className="delete-btn" onClick={() => handleDelete(payment._id, payment.payment_no)} title="Cancel" disabled={payment.status === 'completed' || payment.status === 'cancelled'}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {filteredPayments.length > 0 && (
          <div className="pagination">
            <button onClick={() => setCurrentPage(prev => Math.max(prev-1,1))} disabled={currentPage === 1} className="pagination-btn"><ChevronLeft size={16} />Previous</button>
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, filteredTotalPages) }, (_, i) => {
                let pageNum;
                if (filteredTotalPages <= 5) pageNum = i+1;
                else if (currentPage <= 3) pageNum = i+1;
                else if (currentPage >= filteredTotalPages-2) pageNum = filteredTotalPages-4+i;
                else pageNum = currentPage-2+i;
                return <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}>{pageNum}</button>;
              })}
            </div>
            <button onClick={() => setCurrentPage(prev => Math.min(prev+1, filteredTotalPages))} disabled={currentPage === filteredTotalPages} className="pagination-btn">Next<ChevronRight size={16} /></button>
            <div className="pagination-info">Showing {indexOfFirstItem+1} to {Math.min(indexOfLastItem, filteredPayments.length)} of {filteredPayments.length} entries</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierPaymentList;